import { timestempToRelative } from '../../Helper'
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { getFriendRequest, getFriends } from '../../store/friends';
import { getChat, sendChat } from '../../store/chat';
import { getUsers } from '../../store/auth';
import { useTranslation } from 'react-i18next';
import Form from 'react-bootstrap/Form';
import { Button, Modal, Dropdown } from 'react-bootstrap';
import moment from 'moment-timezone';
import DOMPurify from 'dompurify';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoint';
import { toast } from 'react-toastify';
import { useSearchParams, useLocation } from 'react-router-dom';
import GoldSiverHeader from '../HomePage/GoldSiverHeader';
import '../../assets/css/chatAdmin.css';

function Chat({ isStandalone = false }) {
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const roomIdFromUrl = parseInt(searchParams.get('room')) || 1;
    const [currentRoomId, setCurrentRoomId] = useState(roomIdFromUrl);
    const isChatPage = location.pathname.startsWith('/chat') || isStandalone;
    
    const [usersTab, setUsersTab] = useState('friendList');
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const all_chat = useSelector(state => state.chat.all_chat);
    const pinnedMessageFromStore = useSelector(state => state.chat.pinned_message);
    const broadcastsFromStore = useSelector(state => state.chat.broadcasts);
    const roomStatusFromStore = useSelector(state => state.chat.room_status);
    const [message, setMessage] = useState('')
    const online_users = useSelector(state => state.auth.online_users);
    const friends = useSelector(state => state.friend.friends);
    const online_friend_count = useSelector(state => state.friend.online_friend_count);
    const online_user_count = useSelector(state => state.auth.online_user_count);
    const user_data = useSelector(state => state.auth.user_data);
    
    // Admin state
    const [adminLevel, setAdminLevel] = useState(0);
    const [adminPermissions, setAdminPermissions] = useState({});
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModModal, setShowModModal] = useState(false);
    const [modAction, setModAction] = useState('');
    const [modReason, setModReason] = useState('');
    const [modDuration, setModDuration] = useState(10);
    const [showBroadcastModal, setShowBroadcastModal] = useState(false);
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [broadcastType, setBroadcastType] = useState('info');
    const [showPinModal, setShowPinModal] = useState(false);
    const [pinMessage, setPinMessage] = useState('');
    const [userStatuses, setUserStatuses] = useState({}); // { userId: { is_muted, is_banned, mute_info, ban_info } }
    
    // Debug: Log when modals state changes
    useEffect(() => {
        console.log('🔍 [Chat] showPinModal changed to:', showPinModal);
    }, [showPinModal]);
    
    useEffect(() => {
        console.log('🔍 [Chat] showBroadcastModal changed to:', showBroadcastModal);
    }, [showBroadcastModal]);
    
    // Get room status from store
    const chatLocked = roomStatusFromStore?.is_locked == 1;
    const slowMode = roomStatusFromStore?.slow_mode == 1;
    const slowModeSeconds = roomStatusFromStore?.slow_mode_seconds || 5;
    
    const chatContainerRef = useRef(null);
    
    // Determine direction based on language
    const isRTL = i18n.language === 'he';
    const chatDirection = isRTL ? 'rtl' : 'ltr';
    
    const handleUsersTab = (name) => {
        setUsersTab(name)
    }
    
    // Fetch admin info
    const fetchAdminInfo = async () => {
        try {
            console.log('🔍 Fetching admin info...');
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, { action: 'get_admin_info' });
            console.log('📊 Admin info response:', response.data);
            if (response.data.success) {
                console.log('✅ Admin level:', response.data.admin_level);
                setAdminLevel(response.data.admin_level);
                setAdminPermissions(response.data.permissions);
            }
        } catch (error) {
            console.error('❌ Error fetching admin info:', error.response?.data || error.message);
            // Not an admin, that's fine
            setAdminLevel(0);
        }
    };
    
    
    const getData = async () => {
        try {
            // Update URL if room changed
            if (currentRoomId !== roomIdFromUrl) {
                setCurrentRoomId(roomIdFromUrl);
            }
            
            // Get chat messages for current room
            await dispatch(getChat({ room_id: currentRoomId })).unwrap();
            
            const intervalId = setInterval(() => {
                try {
                    dispatch(getChat({ room_id: currentRoomId })).unwrap();
                } catch (error) {
                    console.error("Error refreshing chat:", error);
                }
            }, 5000);
            
            await dispatch(getUsers()).unwrap();
            await dispatch(getFriends()).unwrap();
            
            // Fetch admin info
            await fetchAdminInfo();
            
            return () => clearInterval(intervalId);
        } catch (error) {
            console.error("Error loading initial data:", error);
        }
    }
    
    // Update room when URL changes
    useEffect(() => {
        const room = parseInt(searchParams.get('room')) || 1;
        if (room !== currentRoomId) {
            setCurrentRoomId(room);
            dispatch(getChat({ room_id: room }));
        }
    }, [searchParams, currentRoomId, dispatch]);
    
    const sanitizeMessage = (msg) => {
        if (!msg || typeof msg !== 'string') return '';
        
        let clean = DOMPurify.sanitize(msg, { 
            ALLOWED_TAGS: [],
            ALLOWED_ATTR: []
        });
        
        clean = clean.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
        clean = clean.replace(/javascript:/gi, '');
        clean = clean.replace(/on\w+\s*=/gi, '');
        
        if (clean.length > 500) {
            clean = clean.substring(0, 500);
        }
        
        return clean.trim();
    }

    const handleSubmit = async () => {
        const cleanMessage = sanitizeMessage(message);
        
        if (!cleanMessage || cleanMessage.length === 0) {
            return;
        }
        
        if (chatLocked && adminLevel < 2) {
            toast.error(t('Chat is locked'));
            return;
        }
        
        const data = {
            name: user_data.username,
            msg: cleanMessage,
            admin: adminLevel > 0,
            room_id: currentRoomId
        }
        
        try {
            await dispatch(sendChat(data)).unwrap();
            setMessage('');
            dispatch(getChat({ room_id: currentRoomId }));
        } catch (error) {
            // Error is already handled by sendChat thunk, but we can add specific handling here
            const errorMsg = error?.response?.data?.error || error?.message || 'Failed to send message';
            if (errorMsg.includes('muted') || errorMsg.includes('banned') || errorMsg.includes('locked') || errorMsg.includes('Slow mode')) {
                // These errors are already shown by toast in sendChat, but refresh chat to update status
                dispatch(getChat({ room_id: currentRoomId }));
            }
        }
    }
    
    const handleEnter = (e) => {
        if (e.charCode === 13) {
            e.preventDefault();
            handleSubmit();
        }
    }
    
    // ==================== MODERATION ACTIONS ====================
    
    const handleModAction = async () => {
        if (!selectedUser || !modAction) return;
        
        try {
            let actionData = {
                action: modAction,
                user_id: selectedUser.user_id,
                reason: modReason
            };
            
            if (modAction === 'mute_user') {
                actionData.duration = modDuration;
            } else if (modAction === 'ban_user') {
                actionData.duration_days = modDuration;
                actionData.ban_type = modDuration > 7 ? 'permanent' : 'temporary';
            }
            
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, actionData);
            
            if (response.data.success) {
                toast.success(response.data.message);
                setShowModModal(false);
                setModReason('');
                setModDuration(10);
                
                // Update user status after action
                if (selectedUser?.user_id) {
                    const status = await handleCheckUserStatus(selectedUser.user_id);
                    setUserStatuses(prev => ({ ...prev, [selectedUser.user_id]: status }));
                }
                
                dispatch(getChat({ room_id: currentRoomId }));
            } else {
                toast.error(response.data.error);
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Action failed');
        }
    };
    
    const handleDeleteMessage = async (messageId) => {
        if (!window.confirm(t('Are you sure you want to delete this message?'))) return;
        
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'delete_message',
                message_id: messageId,
                reason: 'Deleted by moderator'
            });
            
            if (response.data.success) {
                toast.success(t('Message deleted'));
                dispatch(getChat({ room_id: currentRoomId }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to delete');
        }
    };
    
    const handleUnmute = async (userId) => {
        if (!window.confirm(t('Are you sure you want to unmute this user?'))) return;
        
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'unmute_user',
                user_id: userId
            });
            
            if (response.data.success) {
                toast.success(response.data.message || t('User unmuted'));
                dispatch(getChat({ room_id: currentRoomId }));
                // Update user status
                const status = await handleCheckUserStatus(userId);
                setUserStatuses(prev => ({ ...prev, [userId]: status }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to unmute');
        }
    };
    
    const handleUnban = async (userId) => {
        if (!window.confirm(t('Are you sure you want to unban this user?'))) return;
        
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'unban_user',
                user_id: userId,
                reason: 'Unbanned by admin'
            });
            
            if (response.data.success) {
                toast.success(response.data.message || t('User unbanned'));
                dispatch(getChat({ room_id: currentRoomId }));
                // Update user status
                const status = await handleCheckUserStatus(userId);
                setUserStatuses(prev => ({ ...prev, [userId]: status }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to unban');
        }
    };
    
    const handleCheckUserStatus = async (userId) => {
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'check_user_status',
                user_id: userId
            });
            
            if (response.data.success) {
                return {
                    is_muted: response.data.is_muted || false,
                    is_banned: response.data.is_banned || false,
                    mute_info: response.data.mute_info,
                    ban_info: response.data.ban_info
                };
            }
        } catch (error) {
            console.error('Error checking user status:', error);
        }
        return { is_muted: false, is_banned: false };
    };
    
    const handleLockChat = async () => {
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: chatLocked ? 'unlock_chat' : 'lock_chat',
                room_id: currentRoomId
            });
            
            if (response.data.success) {
                toast.success(response.data.message);
                // Refresh chat to get updated room status
                dispatch(getChat({ room_id: currentRoomId }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed');
        }
    };
    
    const handleSlowMode = async () => {
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'slow_mode',
                room_id: currentRoomId,
                enabled: slowMode ? 0 : 1,
                seconds: slowModeSeconds
            });
            
            if (response.data.success) {
                toast.success(response.data.message);
                // Refresh chat to get updated room status
                dispatch(getChat({ room_id: currentRoomId }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed');
        }
    };
    
    const handlePinMessage = async () => {
        if (!pinMessage.trim()) {
            toast.error(t('Please enter a message'));
            return;
        }
        
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'pin_message',
                room_id: currentRoomId,
                message: pinMessage,
                expires_hours: 24
            });
            
            if (response.data.success) {
                toast.success(t('Message pinned'));
                setShowPinModal(false);
                setPinMessage('');
                // Refresh chat to get updated pinned message
                dispatch(getChat({ room_id: currentRoomId }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed');
        }
    };
    
    const handleUnpinMessage = async () => {
        if (!window.confirm(t('Are you sure you want to unpin this message?'))) return;
        
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'unpin_message',
                room_id: currentRoomId
            });
            
            if (response.data.success) {
                toast.success(t('Message unpinned'));
                // Refresh chat to get updated pinned message
                dispatch(getChat({ room_id: currentRoomId }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed');
        }
    };
    
    const handleBroadcast = async () => {
        if (!broadcastMessage.trim()) {
            toast.error(t('Please enter a message'));
            return;
        }
        
        try {
            const response = await axiosInstance.post(API_ENDPOINTS.CHAT_MODERATION, {
                action: 'broadcast',
                message: broadcastMessage,
                broadcast_type: broadcastType,
                is_global: 1,
                expires_hours: 24
            });
            
            if (response.data.success) {
                toast.success(t('Broadcast sent'));
                setShowBroadcastModal(false);
                setBroadcastMessage('');
                // Refresh chat to get updated broadcasts
                dispatch(getChat({ room_id: currentRoomId }));
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed');
        }
    };
    
    const openModModal = (action, user) => {
        setModAction(action);
        setSelectedUser(user);
        setShowModModal(true);
    };
    
    // Get admin level label
    const getAdminLabel = (level) => {
        switch(level) {
            case 1: return { text: t('Helper'), class: 'admin-helper' };
            case 2: return { text: t('Senior Mod'), class: 'admin-senior' };
            case 3: return { text: t('Owner'), class: 'admin-owner' };
            default: return null;
        }
    };
    
    useEffect(() => {
        const cleanup = getData();
        
        return () => {
            if (cleanup && typeof cleanup === 'function') {
                cleanup();
            }
        };
    }, []);
    
    // Update user statuses for admin users
    useEffect(() => {
        if (adminLevel < 1) return;
        
        const updateUserStatuses = async () => {
            const allUserIds = [
                ...(online_users?.map(u => u.acc_id) || []),
                ...(friends?.map(f => f.friend_id) || [])
            ];
            
            const statusMap = {};
            for (const userId of allUserIds) {
                if (userId) {
                    try {
                        const status = await handleCheckUserStatus(userId);
                        statusMap[userId] = status;
                    } catch (error) {
                        console.error(`Error checking status for user ${userId}:`, error);
                    }
                }
            }
            setUserStatuses(statusMap);
        };
        
        if ((online_users && online_users.length > 0) || (friends && friends.length > 0)) {
            updateUserStatuses();
        }
    }, [online_users, friends, adminLevel]);
    
    // If this is the standalone chat page, wrap with header/footer and container
    if (isChatPage) {
        return (
            
                <GoldSiverHeader previous='/home' title={roomStatusFromStore?.room_name || `Chat Room ${currentRoomId}`} showWrapper={true}>
                    <section className="mock-area pt-0">
                <div className="container">
                {/* Admin Bar */}
                {adminLevel > 0 && (
                    <div className="chat-admin-bar">
                        <div className="admin-badge-container">
                            <span className={`admin-badge ${getAdminLabel(adminLevel)?.class}`}>
                                {getAdminLabel(adminLevel)?.text}
                            </span>
                        </div>
                        <div className="admin-actions">
                            {adminLevel >= 2 && (
                                <>
                                    <button 
                                        className={`admin-btn ${chatLocked ? 'active' : ''}`}
                                        onClick={handleLockChat}
                                        title={chatLocked ? t('Unlock Chat') : t('Lock Chat')}
                                    >
                                        {chatLocked ? '🔓' : '🔒'}
                                    </button>
                                    <button 
                                        className={`admin-btn ${slowMode ? 'active' : ''}`}
                                        onClick={handleSlowMode}
                                        title={t('Slow Mode')}
                                    >
                                        🐌
                                    </button>
                                    <button 
                                        className="admin-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            console.log('📌 Pin button clicked, opening modal...');
                                            setShowPinModal(true);
                                        }}
                                        title={t('Pin Message')}
                                    >
                                        📌
                                    </button>
                                </>
                            )}
                            {adminLevel >= 3 && (
                                <button 
                                    className="admin-btn broadcast-btn"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('📢 Broadcast button clicked, opening modal...');
                                        setShowBroadcastModal(true);
                                    }}
                                    title={t('Broadcast')}
                                >
                                    📢
                                </button>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Pinned Message */}
                {pinnedMessageFromStore && (
                    <div className="pinned-message">
                        <span className="pin-icon">📌</span>
                        <span className="pin-text">{pinnedMessageFromStore.message_text}</span>
                        <span className="pin-by">- {pinnedMessageFromStore.pinned_by_name}</span>
                        {adminLevel >= 2 && (
                            <button 
                                className="pin-delete-btn"
                                onClick={() => handleUnpinMessage()}
                                title={t('Unpin Message')}
                                style={{
                                    marginLeft: '10px',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}
                
                {/* Broadcasts */}
                {broadcastsFromStore && broadcastsFromStore.length > 0 && (
                    <div className="broadcasts-container">
                        {broadcastsFromStore.slice(0, 3).map((broadcast, i) => (
                            <div key={i} className={`broadcast-item broadcast-${broadcast.broadcast_type}`}>
                                <span className="broadcast-icon">
                                    {broadcast.broadcast_type === 'warning' ? '⚠️' : 
                                     broadcast.broadcast_type === 'event' ? '🎉' : 
                                     broadcast.broadcast_type === 'maintenance' ? '🔧' : 'ℹ️'}
                                </span>
                                <span className="broadcast-text">{broadcast.message}</span>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Chat Locked Notice */}
                {chatLocked && (
                    <div className="chat-locked-notice">
                        🔒 {t('Chat is currently locked by a moderator')}
                    </div>
                )}
                
                <div className="mock-item3" style={{ direction: chatDirection }}>
                    <div className="mock-item3-inner">
                        <div className="mock-item3-inner2">
                            <ul>
                                <li key="friends-count">{t('friends')} <span key="friend-count-num">{online_friend_count}</span><strong key="friend-total">/{friends?.length}</strong></li>
                                <li key="online-count">{t('online')} <span key="online-count-num">{online_user_count}</span></li>
                            </ul>
                        </div>
                        <div className="mock-item3-inner3">
                            <ul>
                                <li key="friendList"><a className={(usersTab == 'friendList' ? 'usertab-active' : 'usertab') + ' cursor-pointer'} onClick={() => { handleUsersTab('friendList') }}>{t('friendList')}</a></li>
                                <li key="all"><a className={(usersTab == 'all' ? 'usertab-active' : 'usertab') + ' cursor-pointer'} onClick={() => { handleUsersTab('all') }}>{t('all')}</a></li>
                            </ul>
                        </div>
                        <div className="mock-item3-inner4">
                            {usersTab == 'friendList' && friends?.map((item, i) => (
                                <div key={i} className="mock-item3-inner5">
                                    <div className="mock-item3-inner6">
                                        <p>
                                            <img key={`friend-img-${i}`} src="/images/mock-14.png" alt="" /> 
                                            {item.friend_name}
                                        </p>
                                    </div>
                                    <div className="mock-item3-inner7">
                                        <h2 key={`status-${i}`}>{item.online_status}</h2>
                                        {/* Admin actions on users */}
                                        {adminLevel >= 1 && (
                                            <Dropdown className="user-actions-dropdown">
                                                <Dropdown.Toggle variant="link" size="sm">⚙️</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => openModModal('warn_user', { user_id: item.friend_id, username: item.friend_name })}>
                                                        ⚠️ {t('Warn')}
                                                    </Dropdown.Item>
                                                    {userStatuses[item.friend_id]?.is_muted ? (
                                                        <Dropdown.Item onClick={() => handleUnmute(item.friend_id)}>
                                                            🔊 {t('Unmute')}
                                                        </Dropdown.Item>
                                                    ) : (
                                                        <Dropdown.Item onClick={() => openModModal('mute_user', { user_id: item.friend_id, username: item.friend_name })}>
                                                            🔇 {t('Mute')}
                                                        </Dropdown.Item>
                                                    )}
                                                    {adminLevel >= 2 && (
                                                        <>
                                                            {userStatuses[item.friend_id]?.is_banned ? (
                                                                adminLevel >= 3 && (
                                                                    <Dropdown.Item onClick={() => handleUnban(item.friend_id)}>
                                                                        ✅ {t('Unban')}
                                                                    </Dropdown.Item>
                                                                )
                                                            ) : (
                                                                <Dropdown.Item onClick={() => openModModal('ban_user', { user_id: item.friend_id, username: item.friend_name })}>
                                                                    🚫 {t('Ban')}
                                                                </Dropdown.Item>
                                                            )}
                                                        </>
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {usersTab == 'friendList' && friends?.length == 0 && <div key="no-friends" className='notfound'>
                                {t('no Data')}
                            </div>}
                            {usersTab == 'all' && online_users?.length == 0 && <div key="no-users" className='notfound'>
                                {t('no Data')}
                            </div>}
                            {usersTab == 'all' && online_users?.map((item, i) => (
                                <div key={i} className="mock-item3-inner5">
                                    <div className="mock-item3-inner6">
                                        <p>
                                            <img key={`user-img-${i}`} src="/images/mock-14.png" alt="" /> 
                                            {item.username}
                                        </p>
                                    </div>
                                    <div className="mock-item3-inner7">
                                        {/* Admin actions on users */}
                                        {adminLevel >= 1 && (
                                            <Dropdown className="user-actions-dropdown">
                                                <Dropdown.Toggle variant="link" size="sm">⚙️</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => openModModal('warn_user', { user_id: item.acc_id, username: item.username })}>
                                                        ⚠️ {t('Warn')}
                                                    </Dropdown.Item>
                                                    {userStatuses[item.acc_id]?.is_muted ? (
                                                        <Dropdown.Item onClick={() => handleUnmute(item.acc_id)}>
                                                            🔊 {t('Unmute')}
                                                        </Dropdown.Item>
                                                    ) : (
                                                        <Dropdown.Item onClick={() => openModModal('mute_user', { user_id: item.acc_id, username: item.username })}>
                                                            🔇 {t('Mute')}
                                                        </Dropdown.Item>
                                                    )}
                                                    {adminLevel >= 2 && (
                                                        <>
                                                            {userStatuses[item.acc_id]?.is_banned ? (
                                                                adminLevel >= 3 && (
                                                                    <Dropdown.Item onClick={() => handleUnban(item.acc_id)}>
                                                                        ✅ {t('Unban')}
                                                                    </Dropdown.Item>
                                                                )
                                                            ) : (
                                                                <Dropdown.Item onClick={() => openModModal('ban_user', { user_id: item.acc_id, username: item.username })}>
                                                                    🚫 {t('Ban')}
                                                                </Dropdown.Item>
                                                            )}
                                                        </>
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mock-item3-inner9" style={{ direction: chatDirection }}>
                        <div className="mock-item3-inner10">
                            <div className="mock-item3-inner4" ref={chatContainerRef} style={{ direction: chatDirection }}>
                                {all_chat.map((item, index) => {
                                    let decodedMessage = item.msg || '';
                                    try {
                                        if (item.msg && item.msg.includes('%')) {
                                            decodedMessage = decodeURIComponent(item.msg);
                                        }
                                    } catch (e) {
                                        console.error("Error decoding message:", e);
                                    }
                                    
                                    const safeMessage = DOMPurify.sanitize(decodedMessage, {
                                        ALLOWED_TAGS: [],
                                        ALLOWED_ATTR: []
                                    });
                                    
                                    const safeName = DOMPurify.sanitize(item.name || '', {
                                        ALLOWED_TAGS: [],
                                        ALLOWED_ATTR: []
                                    });
                                    
                                    // The timestamp from API is already converted to Israel timezone
                                    // Just display it as is
                                    const displayTime = item.posted || '';
                                    
                                    return (
                                        <div key={index} className="mock-item3-inner11">
                                            <h2 className='d-flex justify-content-between ml-2'> 
                                                <span key={`posted-${index}`}>{displayTime}</span>
                                                <span key={`name-${index}`}>{safeName}</span>
                                            </h2>
                                            <p key={`msg-${index}`}>{safeMessage}</p>
                                            
                                            {/* Admin message actions */}
                                            {adminLevel >= 1 && (
                                                <div className="message-actions">
                                                    <button 
                                                        className="action-btn delete-btn"
                                                        onClick={() => handleDeleteMessage(item.id)}
                                                        title={t('Delete')}
                                                    >
                                                        🗑️
                                                    </button>
                                                    <button 
                                                        className="action-btn warn-btn"
                                                        onClick={() => openModModal('warn_user', { user_id: item.user_id, username: safeName })}
                                                        title={t('Warn')}
                                                    >
                                                        ⚠️
                                                    </button>
                                                    <button 
                                                        className="action-btn mute-btn"
                                                        onClick={() => openModModal('mute_user', { user_id: item.user_id, username: safeName })}
                                                        title={t('Mute')}
                                                    >
                                                        🔇
                                                    </button>
                                                </div>
                                            )}
                                            
                                            <img key={`chat-img-${index}`} src="/images/mock-19.png" alt="" />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className='chat-input d-flex'>
                                <Form.Control
                                    key="message-input"
                                    type="text"
                                    id="inputPassword5"
                                    value={message}
                                    onChange={(e) => { setMessage(e.target.value) }}
                                    aria-describedby="passwordHelpBlock"
                                    onKeyPress={handleEnter}
                                    placeholder={chatLocked && adminLevel < 2 ? t('Chat is locked') : t('Type a message...')}
                                    disabled={chatLocked && adminLevel < 2}
                                />
                                <Button 
                                    key="send-button" 
                                    variant="primary" 
                                    onClick={handleSubmit}
                                    disabled={chatLocked && adminLevel < 2}
                                >
                                    <img src="/images/mock-18.png" alt="" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
                </div>
            
            {/* Moderation Modal */}
            <Modal show={showModModal} onHide={() => setShowModModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modAction === 'warn_user' && `⚠️ ${t('Warn User')}`}
                        {modAction === 'mute_user' && `🔇 ${t('Mute User')}`}
                        {modAction === 'ban_user' && `🚫 ${t('Ban User')}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p><strong>{t('User')}:</strong> {selectedUser?.username}</p>
                    
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Reason')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={modReason}
                            onChange={(e) => setModReason(e.target.value)}
                            placeholder={t('Enter reason...')}
                        />
                    </Form.Group>
                    
                    {modAction === 'mute_user' && (
                        <Form.Group className="mb-3">
                            <Form.Label>{t('Duration (minutes)')}</Form.Label>
                            <Form.Select 
                                value={modDuration} 
                                onChange={(e) => setModDuration(parseInt(e.target.value))}
                            >
                                <option value="5">5 {t('minutes')}</option>
                                <option value="10">10 {t('minutes')}</option>
                                {adminLevel >= 2 && (
                                    <>
                                        <option value="30">30 {t('minutes')}</option>
                                        <option value="60">1 {t('hour')}</option>
                                        <option value="360">6 {t('hours')}</option>
                                        <option value="720">12 {t('hours')}</option>
                                        <option value="1440">24 {t('hours')}</option>
                                    </>
                                )}
                            </Form.Select>
                        </Form.Group>
                    )}
                    
                    {modAction === 'ban_user' && (
                        <Form.Group className="mb-3">
                            <Form.Label>{t('Duration (days)')}</Form.Label>
                            <Form.Select 
                                value={modDuration} 
                                onChange={(e) => setModDuration(parseInt(e.target.value))}
                            >
                                <option value="1">1 {t('day')}</option>
                                <option value="3">3 {t('days')}</option>
                                <option value="7">7 {t('days')}</option>
                                {adminLevel >= 3 && (
                                    <>
                                        <option value="30">30 {t('days')}</option>
                                        <option value="9999">{t('Permanent')}</option>
                                    </>
                                )}
                            </Form.Select>
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModModal(false)}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="danger" onClick={handleModAction}>
                        {t('Confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Pin Message Modal */}
            {console.log('🔍 [Chat] Rendering Pin Modal, showPinModal:', showPinModal)}
            <Modal 
                show={showPinModal} 
                onHide={() => {
                    console.log('📌 Pin modal closing...');
                    setShowPinModal(false);
                }} 
                centered
                backdrop="static"
                style={{ zIndex: 10000 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>📌 {t('Pin Message')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Message')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={pinMessage}
                            onChange={(e) => setPinMessage(e.target.value)}
                            placeholder={t('Enter message to pin...')}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPinModal(false)}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={handlePinMessage}>
                        {t('Pin')}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Broadcast Modal */}
            {console.log('🔍 [Chat] Rendering Broadcast Modal, showBroadcastModal:', showBroadcastModal)}
            <Modal 
                show={showBroadcastModal} 
                onHide={() => {
                    console.log('📢 Broadcast modal closing...');
                    setShowBroadcastModal(false);
                }} 
                centered
                backdrop="static"
                style={{ zIndex: 10000 }}
            >
                <Modal.Header closeButton>
                    <Modal.Title>📢 {t('Send Broadcast')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Type')}</Form.Label>
                        <Form.Select 
                            value={broadcastType} 
                            onChange={(e) => setBroadcastType(e.target.value)}
                        >
                            <option value="info">ℹ️ {t('Info')}</option>
                            <option value="warning">⚠️ {t('Warning')}</option>
                            <option value="event">🎉 {t('Event')}</option>
                            <option value="maintenance">🔧 {t('Maintenance')}</option>
                        </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>{t('Message')}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={broadcastMessage}
                            onChange={(e) => setBroadcastMessage(e.target.value)}
                            placeholder={t('Enter broadcast message...')}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowBroadcastModal(false)}>
                        {t('Cancel')}
                    </Button>
                    <Button variant="success" onClick={handleBroadcast}>
                        {t('Send Broadcast')}
                    </Button>
                </Modal.Footer>
            </Modal>
                    </section>
                </GoldSiverHeader>
               
        
        );
    }
    
    // If this is embedded via ChatWrapper, render without header/footer
    return (
        <section className="mock-area pt-0">
            <div className="container">
                {/* Admin Bar */}
                {adminLevel > 0 && (
                    <div className="chat-admin-bar">
                        <div className="admin-badge-container">
                            <span className={`admin-badge ${getAdminLabel(adminLevel)?.class}`}>
                                {getAdminLabel(adminLevel)?.text}
                            </span>
                        </div>
                        <div className="admin-actions">
                            {adminLevel >= 2 && (
                                <>
                                    <button 
                                        className={`admin-btn ${chatLocked ? 'active' : ''}`}
                                        onClick={handleLockChat}
                                        title={chatLocked ? t('Unlock Chat') : t('Lock Chat')}
                                    >
                                        {chatLocked ? '🔓' : '🔒'}
                                    </button>
                                    <button 
                                        className={`admin-btn ${slowMode ? 'active' : ''}`}
                                        onClick={handleSlowMode}
                                        title={t('Slow Mode')}
                                    >
                                        🐌
                                    </button>
                                    <button 
                                        className="admin-btn"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            console.log('📌 Pin button clicked, opening modal...');
                                            setShowPinModal(true);
                                        }}
                                        title={t('Pin Message')}
                                    >
                                        📌
                                    </button>
                                </>
                            )}
                            {adminLevel >= 3 && (
                                <button 
                                    className="admin-btn broadcast-btn"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        console.log('📢 Broadcast button clicked, opening modal...');
                                        setShowBroadcastModal(true);
                                    }}
                                    title={t('Broadcast')}
                                >
                                    📢
                                </button>
                            )}
                        </div>
                    </div>
                )}
                
                {/* Pinned Message */}
                {pinnedMessageFromStore && (
                    <div className="pinned-message">
                        <span className="pin-icon">📌</span>
                        <span className="pin-text">{pinnedMessageFromStore.message_text}</span>
                        <span className="pin-by">- {pinnedMessageFromStore.pinned_by_name}</span>
                        {adminLevel >= 2 && (
                            <button 
                                className="pin-delete-btn"
                                onClick={() => handleUnpinMessage()}
                                title={t('Unpin Message')}
                                style={{
                                    marginLeft: '10px',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: '16px'
                                }}
                            >
                                ✕
                            </button>
                        )}
                    </div>
                )}
                
                {/* Broadcasts */}
                {broadcastsFromStore && broadcastsFromStore.length > 0 && (
                    <div className="broadcasts-container">
                        {broadcastsFromStore.slice(0, 3).map((broadcast, i) => (
                            <div key={i} className={`broadcast-item broadcast-${broadcast.broadcast_type}`}>
                                <span className="broadcast-icon">
                                    {broadcast.broadcast_type === 'warning' ? '⚠️' : 
                                     broadcast.broadcast_type === 'event' ? '🎉' : 
                                     broadcast.broadcast_type === 'maintenance' ? '🔧' : 'ℹ️'}
                                </span>
                                <span className="broadcast-text">{broadcast.message}</span>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Chat Locked Notice */}
                {chatLocked && (
                    <div className="chat-locked-notice">
                        🔒 {t('Chat is currently locked by a moderator')}
                    </div>
                )}
                
                <div className="mock-item3" style={{ direction: chatDirection }}>
                    <div className="mock-item3-inner">
                        <div className="mock-item3-inner2">
                            <ul>
                                <li key="friends-count">{t('friends')} <span key="friend-count-num">{online_friend_count}</span><strong key="friend-total">/{friends?.length}</strong></li>
                                <li key="online-count">{t('online')} <span key="online-count-num">{online_user_count}</span></li>
                            </ul>
                        </div>
                        <div className="mock-item3-inner3">
                            <ul>
                                <li key="friendList"><a className={(usersTab == 'friendList' ? 'usertab-active' : 'usertab') + ' cursor-pointer'} onClick={() => { handleUsersTab('friendList') }}>{t('friendList')}</a></li>
                                <li key="all"><a className={(usersTab == 'all' ? 'usertab-active' : 'usertab') + ' cursor-pointer'} onClick={() => { handleUsersTab('all') }}>{t('all')}</a></li>
                            </ul>
                        </div>
                        <div className="mock-item3-inner4">
                            {usersTab == 'friendList' && friends?.map((item, i) => (
                                <div key={i} className="mock-item3-inner5">
                                    <div className="mock-item3-inner6">
                                        <p>
                                            <img key={`friend-img-${i}`} src="/images/mock-14.png" alt="" /> 
                                            {item.friend_name}
                                        </p>
                                    </div>
                                    <div className="mock-item3-inner7">
                                        <h2 key={`status-${i}`}>{item.online_status}</h2>
                                        {/* Admin actions on users */}
                                        {adminLevel >= 1 && (
                                            <Dropdown className="user-actions-dropdown">
                                                <Dropdown.Toggle variant="link" size="sm">⚙️</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => openModModal('warn_user', { user_id: item.friend_id, username: item.friend_name })}>
                                                        ⚠️ {t('Warn')}
                                                    </Dropdown.Item>
                                                    {userStatuses[item.friend_id]?.is_muted ? (
                                                        <Dropdown.Item onClick={() => handleUnmute(item.friend_id)}>
                                                            🔊 {t('Unmute')}
                                                        </Dropdown.Item>
                                                    ) : (
                                                        <Dropdown.Item onClick={() => openModModal('mute_user', { user_id: item.friend_id, username: item.friend_name })}>
                                                            🔇 {t('Mute')}
                                                        </Dropdown.Item>
                                                    )}
                                                    {adminLevel >= 2 && (
                                                        <>
                                                            {userStatuses[item.friend_id]?.is_banned ? (
                                                                adminLevel >= 3 && (
                                                                    <Dropdown.Item onClick={() => handleUnban(item.friend_id)}>
                                                                        ✅ {t('Unban')}
                                                                    </Dropdown.Item>
                                                                )
                                                            ) : (
                                                                <Dropdown.Item onClick={() => openModModal('ban_user', { user_id: item.friend_id, username: item.friend_name })}>
                                                                    🚫 {t('Ban')}
                                                                </Dropdown.Item>
                                                            )}
                                                        </>
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                    </div>
                                </div>
                            ))}
                            {usersTab == 'friendList' && friends?.length == 0 && <div key="no-friends" className='notfound'>
                                {t('no Data')}
                            </div>}
                            {usersTab == 'all' && online_users?.length == 0 && <div key="no-users" className='notfound'>
                                {t('no Data')}
                            </div>}
                            {usersTab == 'all' && online_users?.map((item, i) => (
                                <div key={i} className="mock-item3-inner5">
                                    <div className="mock-item3-inner6">
                                        <p>
                                            <img key={`user-img-${i}`} src="/images/mock-14.png" alt="" /> 
                                            {item.username}
                                        </p>
                                    </div>
                                    <div className="mock-item3-inner7">
                                        {/* Admin actions on users */}
                                        {adminLevel >= 1 && (
                                            <Dropdown className="user-actions-dropdown">
                                                <Dropdown.Toggle variant="link" size="sm">⚙️</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item onClick={() => openModModal('warn_user', { user_id: item.acc_id, username: item.username })}>
                                                        ⚠️ {t('Warn')}
                                                    </Dropdown.Item>
                                                    {userStatuses[item.acc_id]?.is_muted ? (
                                                        <Dropdown.Item onClick={() => handleUnmute(item.acc_id)}>
                                                            🔊 {t('Unmute')}
                                                        </Dropdown.Item>
                                                    ) : (
                                                        <Dropdown.Item onClick={() => openModModal('mute_user', { user_id: item.acc_id, username: item.username })}>
                                                            🔇 {t('Mute')}
                                                        </Dropdown.Item>
                                                    )}
                                                    {adminLevel >= 2 && (
                                                        <>
                                                            {userStatuses[item.acc_id]?.is_banned ? (
                                                                adminLevel >= 3 && (
                                                                    <Dropdown.Item onClick={() => handleUnban(item.acc_id)}>
                                                                        ✅ {t('Unban')}
                                                                    </Dropdown.Item>
                                                                )
                                                            ) : (
                                                                <Dropdown.Item onClick={() => openModModal('ban_user', { user_id: item.acc_id, username: item.username })}>
                                                                    🚫 {t('Ban')}
                                                                </Dropdown.Item>
                                                            )}
                                                        </>
                                                    )}
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mock-item3-inner9" style={{ direction: chatDirection }}>
                        <div className="mock-item3-inner10">
                            <div className="mock-item3-inner4" ref={chatContainerRef} style={{ direction: chatDirection }}>
                                {all_chat.map((item, index) => {
                                    let decodedMessage = item.msg || '';
                                    try {
                                        if (item.msg && item.msg.includes('%')) {
                                            decodedMessage = decodeURIComponent(item.msg);
                                        }
                                    } catch (e) {
                                        console.error("Error decoding message:", e);
                                    }
                                    
                                    const safeMessage = DOMPurify.sanitize(decodedMessage, {
                                        ALLOWED_TAGS: [],
                                        ALLOWED_ATTR: []
                                    });
                                    
                                    const safeName = DOMPurify.sanitize(item.name || '', {
                                        ALLOWED_TAGS: [],
                                        ALLOWED_ATTR: []
                                    });
                                    
                                    // The timestamp from API is already converted to Israel timezone
                                    // Just display it as is
                                    const displayTime = item.posted || '';
                                    
                                    return (
                                        <div key={index} className="mock-item3-inner11">
                                            <h2 className='d-flex justify-content-between ml-2'> 
                                                <span key={`posted-${index}`}>{displayTime}</span>
                                                <span key={`name-${index}`}>{safeName}</span>
                                            </h2>
                                            <p key={`msg-${index}`}>{safeMessage}</p>
                                            
                                            {/* Admin message actions */}
                                            {adminLevel >= 1 && (
                                                <div className="message-actions">
                                                    <button 
                                                        className="action-btn delete-btn"
                                                        onClick={() => handleDeleteMessage(item.id)}
                                                        title={t('Delete')}
                                                    >
                                                        🗑️
                                                    </button>
                                                    <button 
                                                        className="action-btn warn-btn"
                                                        onClick={() => openModModal('warn_user', { user_id: item.user_id, username: safeName })}
                                                        title={t('Warn')}
                                                    >
                                                        ⚠️
                                                    </button>
                                                    <button 
                                                        className="action-btn mute-btn"
                                                        onClick={() => openModModal('mute_user', { user_id: item.user_id, username: safeName })}
                                                        title={t('Mute')}
                                                    >
                                                        🔇
                                                    </button>
                                                </div>
                                            )}
                                            
                                            <img key={`chat-img-${index}`} src="/images/mock-19.png" alt="" />
                                        </div>
                                    );
                                })}
                            </div>
                            <div className='chat-input d-flex'>
                                <Form.Control
                                    key="message-input"
                                    type="text"
                                    id="inputPassword5"
                                    value={message}
                                    onChange={(e) => { setMessage(e.target.value) }}
                                    aria-describedby="passwordHelpBlock"
                                    onKeyPress={handleEnter}
                                    placeholder={chatLocked && adminLevel < 2 ? t('Chat is locked') : t('Type a message...')}
                                    disabled={chatLocked && adminLevel < 2}
                                />
                                <Button 
                                    key="send-button" 
                                    variant="primary" 
                                    onClick={handleSubmit}
                                    disabled={chatLocked && adminLevel < 2}
                                >
                                    <img src="/images/mock-18.png" alt="" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default Chat
